import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import { motion } from "framer-motion";
import { MapPin, Briefcase, DollarSign, Calendar, Send } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE;

const allJobs = [
  "Plumber",
  "Electrician",
  "Lawn Cleaning",
  "Cleaning",
  "Furniture Assembly",
  "Handyman",
  "Painter",
  "Carpet Cleaning",
  "Pressure Washing",
  "TV Mounting",
  "Appliance Repair",
  "Flooring Repair",
  "Pool / Hot Tub Cleaning",

  "Roof Cleaning",
  "Window Cleaning",
  "Junk Removal",
  "Snow Removal",

  "Barber",
  "Haircut & Styling (Women)",
  "Pedicure & Manicure",
  "Threading / Waxing",
  "Makeup Artist (Event)",
  "Bridal Makeup",
  "Facial & Skin Care",
  "At-Home Spa (Massage)",

  "Mechanic",
  "Car Detailing",

  "Event Photographer",
  "Personal Chef",

  "Moving",
  "Child Care",
  "Tutor / Teaching",

  // OPTIONAL SPECIAL ONES YOU HAD — KEEP ONLY IF NEEDED
  // "Respite Worker",
  // "Driver",
  // "Mehndi",
];

export default function CustomerDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const autocompleteRef = useRef(null);

  const [selectedJob, setSelectedJob] = useState(location.state?.selectedService || allJobs[0]);
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [preferredDateTime, setPreferredDateTime] = useState("");
  const [jobAddress, setJobAddress] = useState(localStorage.getItem("userAddress") || "");
  const [editingAddress, setEditingAddress] = useState(false);
  const [message, setMessage] = useState("");
  const isLoggedIn = !!localStorage.getItem("customerId");

  useEffect(() => {
    if (location.state?.selectedService) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handlePostJob = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!isLoggedIn) return navigate("/login");
    if (!preferredDateTime || !jobAddress || budget === "")
      return setMessage("⚠️ Please fill all required fields.");
    if (parseFloat(budget) < 0) return setMessage("❌ Budget can't be negative.");

    const customerId = localStorage.getItem("customerId");
    let lat = localStorage.getItem("userLat");
    let lng = localStorage.getItem("userLng");

    if (!lat || !lng) {
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(jobAddress)}&key=${apiKey}`
        );
        const data = await res.json();
        if (data.status === "OK" && data.results[0]) {
          lat = data.results[0].geometry.location.lat;
          lng = data.results[0].geometry.location.lng;
          localStorage.setItem("userLat", lat);
          localStorage.setItem("userLng", lng);
        } else return setMessage("⚠️ Address could not be verified.");
      } catch (err) {
        console.error("Geocoding failed:", err);
        return setMessage("❌ Error fetching location.");
      }
    }

    try {
      const res = await fetch(`${API_BASE}/api/customers/jobs/check-availability`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId,
          serviceType: selectedJob,
          jobTitle: selectedJob,
          description: description || `${selectedJob} - Scheduled: ${preferredDateTime}`,
          budget: parseFloat(budget),
          location: jobAddress,
          scheduledAt: preferredDateTime,
          email: localStorage.getItem("userEmail"),
          latitude: lat,
          longitude: lng,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        if (data.status === "waitlisted") {
          navigate("/no-workers", {
            state: { reason: data.waitlistReason || "No approved workers nearby." },
          });
        } else {
          navigate("/payment", {
            state: {
              amount: parseFloat(budget) || 50,
              customerId,
              serviceType: selectedJob,
              jobTitle: selectedJob,
              description: description || `${selectedJob} - Scheduled: ${preferredDateTime}`,
              scheduledAt: preferredDateTime,
              location: jobAddress,
              email: localStorage.getItem("userEmail"),
              lat,
              lng,
              workersFound: data.workersFound || 0,
            },
          });
        }
      } else setMessage(data.error || "❌ Failed to check job availability.");
    } catch (err) {
      console.error("❌ Error checking job availability:", err);
      setMessage("❌ Error checking job availability. Please try again.");
    }
  };

  const handlePlaceChange = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.formatted_address || !place.geometry) return;
    const fullAddress = place.formatted_address;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setJobAddress(fullAddress);
    setEditingAddress(false);
    localStorage.setItem("userAddress", fullAddress);
    localStorage.setItem("userLat", lat);
    localStorage.setItem("userLng", lng);
  };

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning 🌅";
    if (hour < 18) return "Good Afternoon ☀️";
    return "Good Evening 🌙";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-200 via-indigo-100 to-blue-300 flex flex-col justify-center p-4 md:p-10">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full bg-white/40 backdrop-blur-xl p-6 md:p-10 rounded-3xl shadow-2xl border border-white/40 max-w-[95rem] mx-auto"
      >
        {/* Greeting Header */}
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-3xl md:text-4xl font-extrabold text-center text-indigo-900 mb-8"
        >
          {greeting()}, ready to book your need?
        </motion.h2>

        {/* Selected Job Banner */}
        {location.state?.selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-indigo-100 border-l-4 border-indigo-500 text-indigo-700 p-4 rounded-xl text-center font-semibold text-lg shadow mb-8"
          >
            You selected:{" "}
            <span className="text-indigo-900">{location.state.selectedService}</span>
          </motion.div>
        )}

        {/* Form */}
        <motion.form
          onSubmit={handlePostJob}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {/* Address */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="col-span-1 md:col-span-3 bg-white/70 p-5 rounded-2xl shadow-md"
          >
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
              <MapPin className="w-4 h-4 text-indigo-700" /> Job Location
            </label>
            {!editingAddress ? (
              <p
                className="text-gray-800 font-medium bg-gray-100 px-4 py-2 rounded-lg cursor-pointer hover:bg-gray-200 transition"
                onClick={() => setEditingAddress(true)}
              >
                {jobAddress || "Click to enter job location"}
              </p>
            ) : (
              <LoadScript
                googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                libraries={["places"]}
              >
                <Autocomplete
                  onLoad={(auto) => (autocompleteRef.current = auto)}
                  onPlaceChanged={handlePlaceChange}
                >
                  <input
                    placeholder="Search new job address..."
                    autoFocus
                    className="w-full p-3 rounded-lg border border-indigo-400"
                  />
                </Autocomplete>
              </LoadScript>
            )}
          </motion.div>

          {/* Job Type */}
          <motion.div whileHover={{ scale: 1.02 }} className="bg-white/70 p-5 rounded-2xl shadow-md">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
              <Briefcase className="w-4 h-4 text-indigo-700" /> Select Job Type
            </label>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="w-full p-3 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-indigo-400"
            >
              {allJobs.map((job, idx) => (
                <option key={idx} value={job}>
                  {job}
                </option>
              ))}
            </select>
          </motion.div>

          {/* Budget */}
          <motion.div whileHover={{ scale: 1.02 }} className="bg-white/70 p-5 rounded-2xl shadow-md">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
              <DollarSign className="w-4 h-4 text-indigo-700" /> Budget ($)
            </label>
            <input
              type="number"
              min="0"
              placeholder="e.g. 150"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="w-full p-3 rounded-lg border border-gray-300"
            />
          </motion.div>

          {/* Date & Time */}
          <motion.div whileHover={{ scale: 1.02 }} className="bg-white/70 p-5 rounded-2xl shadow-md">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
              <Calendar className="w-4 h-4 text-indigo-700" /> Preferred Date & Time
            </label>
            <input
              type="datetime-local"
              value={preferredDateTime}
              onChange={(e) => setPreferredDateTime(e.target.value)}
              className="w-full p-3 rounded-lg border border-gray-300"
            />
          </motion.div>

          {/* Description */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            className="col-span-1 md:col-span-3 bg-white/70 p-5 rounded-2xl shadow-md"
          >
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
              <Briefcase className="w-4 h-4 text-indigo-700" /> Description
            </label>
            <textarea
              placeholder="Describe the job details..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full p-3 rounded-lg border border-gray-300"
            />
          </motion.div>

          {/* Button */}
          <div className="col-span-1 md:col-span-3">
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(79, 70, 229, 0.5)" }}
              whileTap={{ scale: 0.97 }}
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-indigo-700 to-blue-500 text-white font-bold text-lg rounded-full shadow-lg hover:shadow-indigo-500/50 transition-all flex items-center justify-center gap-2"
            >
              <Send className="w-5 h-5" /> Post Your Job
            </motion.button>
            {message && (
              <p className="text-center text-red-600 font-semibold mt-2">{message}</p>
            )}
          </div>
        </motion.form>
      </motion.div>
    </div>
  );
}
